<template>
  <div class="home u-wrapper-panel u-row u-middle u-left">
    <transition name="t-fader">
      <div v-if="!isIntroduction && isHomeScreen" class="u-w8of12 u-w12of12--sm u-pad-x-1of12 u-pad-b-lg u-white">
        <div class="u-row">
          <div class="u-w2of12 u-w4of12--sm">
            <img src="/images/bright__logo--white.png" alt="Bright logo" class="u-block u-full-width"/>
          </div> 
        </div>
        <p class="home__title t-h3 u-marg-t-md u-pad-y-sm u-marg-t-md--sm u-uppercase " v-html="data.titre"/>
        <p class="t-text u-w7of12 u-w8of12--sm u-marg-t-md">{{data.description}}</p>
      </div>
    </transition>
    <transition v-if="!isIntroduction && isHomeScreen && !$device.isTouch" name="t-fader">
      <div class="home__cta u-white u-cursor-pointer" :isWhite="true" :isSmall="true" :isGhost="true" @click="clicked++">
        <span class="t-text--xsmall t-regular">
          {{ ctas[clicked % ctas.length] }}
        </span>
      </div>
    </transition>
    <app-button class="home__cta--mobile" :isWhite="true" :isGhost="true" @click="onNext" v-if="$device.isTouch">
      <span class="t-text">
        Next
      </span>
    </app-button>
    <transition name="t-fader">
      <homepage-animation @readyToSetup="readyToSetup" @finished="onIntroEnded" v-if="isOverlayIntro" />
    </transition>
    <logos
      :position="{
        x: $store.state.global.screen.x,
        y: $device.isMobile ? 10 : 0,
        z: $device.isMobile ? $store.state.global.screen.z + 180 : $store.state.global.screen.z + 100
      }"
      :viewportWidth="visibleViewportScreen"
      :logos="data.brightLogos"
    />
    <screen
      :position="{x: $store.state.global.screen.x, y: $store.state.global.screen.y, z: $store.state.global.screen.z}"
      :src="data.video[0].url"
      :style="{width: `${$store.state.global.screen.width * 10}px`, height: `${$store.state.global.screen.height * 10}px`}"
    />
  </div>
</template>

<script>
import { FitTo } from '@/utils/FitTo'
import { CameraMovement } from '@/utils/CameraMovement'
import { VirtualScroll } from '@/utils/VirtualScroll'
import { visibleViewport } from '@/utils/Maths'
import { Loader } from '@/utils/Loader'
import gsap from 'gsap'

import { webGL } from '@/webGL/WebGL'
import { Ball } from '@/webGL/objects/Ball'
import { Screens } from '@/webGL/objects/Screens'

import HomepageAnimation from '@/components/HomepageAnimation'
import AppButton from '@/components/common/AppButton'
import Screen from '@/components/3D/Screen'
import Logos from '@/components/3D/Logos'

import { envMap } from '@/webGL/objects/EnvMap'

// import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'

import cta from '@/assets/data/cta'

const planePosX = 300
const lightPos = new Vector3(100, 200, 100)

import {
  DirectionalLight,
  PlaneGeometry,
  MeshBasicMaterial,
  AmbientLight,
  CubeTextureLoader,
  Vector3,
  Vector2,
  Box3,
  CameraHelper,
  Color,
  Mesh
} from 'three'

export default {
  name: 'Home',

  metaInfo: {
    link: [
      { rel: 'canonical', href: 'https://brig.ht' }
    ]
  },

  components: {
    HomepageAnimation,
    AppButton,
    Logos,
    Screen
  },

  computed: {
    data () {
      return this.$store.getters['data/homepage']
    }
  },

  data () {
    return {
      isIntroduction: true,
      isHomeScreen: true,
      isOverlayIntro: true,
      visibleViewport: 0,
      visibleViewportScreen: 0,
      clicked: 0,
      ctas: cta,
    } 
  },

  created () {
    webGL.camera.setFocalLength(85)

    this.fitTo = new FitTo(webGL.camera)
    this.ball = new Ball(1.5)
    this.ball.object.position.x = 0
    this.isSetuped = false
    webGL.scene.add(this.ball)

    this.isLeaving = false

    webGL.camera.forceUpdateLook = true
    this.ballPosX = 0
    this.ball.object.position.z = 0
    this.currentUV = new Vector2()
  },

  mounted () {
    this.$root.$el.addEventListener(this.$device.pointermove, this.onPointermove)

    if (!this.$device.isTouch) {
      this.cameraMovement = new CameraMovement(webGL.camera, null, 'free')
    }
    else {
      this.cameraMovement = new CameraMovement(webGL.camera)
    }
    this.virtualScroll = new VirtualScroll({ smooth: .15, clamp: {min: 0} })

    this.maxStep = this.$device.isIphone ? 2 : 3
  },

  beforeDestroy () {
    clearTimeout(this.timeout)
    this.virtualScroll.onDestroy()
    this.ball.onDestroy()

    webGL.scene.remove(this.directional)
    webGL.scene.remove(this.ambient)
    webGL.scene.remove(this.screen)
    webGL.scene.remove(this.ball)
    webGL.scene.remove(this.env)

    if (!this.$device.isIphone) {
      this.screens.onDestroy()
      webGL.scene.remove(this.screens)
    }

    this.$root.$el.removeEventListener(this.$device.pointermove, this.onPointermove)
  },

  methods: {
    async readyToSetup () {
      if (!this.$device.isIphone) {
        this.screens = new Screens(this.data.brightScreensVideo)
        webGL.scene.add(this.screens)
        this.screens.play()
      }
      else {
        this.$hub.emit('homepage:video')
      }
      await this.setup()
      this.onResize()

      // we force base position
      // cause sometimes it fails
      webGL.camera.position.copy(this.cameraPosBase.position)
      webGL.camera.look.copy(this.cameraPosBase.look)
      // if for any reason, went to this.onIntroEnded before onResize fully executed
      // we lunch it again
      this.isSetuped = true
      if (!this.isIntroduction) {
        this.onIntroEnded()
      }
    },

    onIntroEnded () {
      this.isOverlayIntro = false
      this.isIntroduction = false

      if (this.isSetuped) {
        this.virtualScroll.onPrev(this.cameraMovement.onPrev)
        this.virtualScroll.onNext(this.cameraMovement.onNext)
        this.virtualScroll.onSmoothScroll(this.cameraMovement.onScroll)
      }
    },

    setup () {
      this.directional = new DirectionalLight( 0xffffff, .9)
      this.directional.castShadow = true
      this.directional.shadow.camera.bottom = -500
      this.directional.shadow.camera.top = 100
      this.directional.shadow.camera.left = -100
      this.directional.shadow.camera.right = 500
      this.directional.shadow.camera.far = 1000
      this.directional.shadow.camera.near = 0.5
      this.directional.shadow.mapSize.width = 1024
      this.directional.shadow.mapSize.height = 1024
      this.directional.shadow.bias = -0.0004
      this.directional.shadow.camera.updateProjectionMatrix()
      this.directional.position.copy(lightPos)
      webGL.scene.add(this.directional)

      // const helper = new CameraHelper(this.directional.shadow.camera)
      // webGL.scene.add(helper)

      this.ambient = new AmbientLight(0xffffff, .3)
      webGL.scene.add(this.ambient)

      return new Promise(resolve => {
        Loader.load('/objects/environments/v2/homepage--01.glb', false, obj => {
          obj.receiveShadow = true
        }).then(obj => {
          this.env = obj
          this.env.position.y = 1
          this.env.scale.set(20, 20, 20)
          this.env.updateMatrix()

          envMap.applyMap(this.env.children[0])
          webGL.scene.add(this.env)

          const geo = new PlaneGeometry(1000, 1000, 1, 1)
          const mat = new MeshBasicMaterial()
          const mesh = new Mesh(geo, mat)
          mesh.receiveShadow = false
          mesh.visible = false
          mesh.rotation.x = -Math.PI * .5
          mesh.position.x = planePosX

          this.shadedObj = mesh
          webGL.scene.add(this.shadedObj)

          const box = new Box3().makeEmpty()
          box.setFromObject(this.env)
          this.env.position.y = box.min.y
          const size = new Vector3()
          box.getSize(size)
          this.env.position.x = size.x * .25

          requestAnimationFrame(resolve)
        })
      })

    },

    onUpdate() {
      if (this.isIntroduction || !this.isSetuped) {return}

      if (webGL.camera.position.x > this.cameraPosBase.position.x + 20) {
        this.isHomeScreen = false
      }
      else {
        this.isHomeScreen = true
      }

      if (!this.$device.isTouch) {
        if (this.shadedObj) {
          const intersect = this.$device.getIntersect(this.shadedObj)
          if (intersect) {
            this.intersect = this.$device.getIntersect(this.shadedObj)
          }

          if (intersect) {
            this.ball.object.position.x += (intersect.point.x - this.ball.object.position.x) * .1
            this.ball.object.position.z += (intersect.point.z - this.ball.object.position.z) * .1
          }
        }

        if (!this.isLeaving && this.cameraMovement.absoluteProgression > 14) {
          this.isLeaving = true
          this.$router.push({...this.$local('Promise'), params: {slug: this.$store.getters['data/promiseByIndex'](0).slug }})
        }
      }
      else {
        // this.ball.object.position.x += (webGL.camera.position.x - this.ball.object.position.x) * .15
        this.ball.object.position.x += (webGL.camera.look.x - this.ball.object.position.x) * .15
        // console.log(webGL.camera.look.x)

        if (this.cameraMovement.step === this.maxStep && !this.isLeaving) {
          this.isLeaving = true
          this.$router.push({...this.$local('Projects')})
        }
      }
    },

    onResize () {
      const screenBox = new Box3()
        .makeEmpty()
        .expandByPoint(
          new Vector3(this.$store.state.global.screen.x - this.$store.state.global.screen.width * .5, 0, this.$store.state.global.screen.z)
        )
        .expandByPoint(
          new Vector3(this.$store.state.global.screen.x + this.$store.state.global.screen.width * .5, this.$store.state.global.screen.y + this.$store.state.global.screen.height * .5, this.$store.state.global.screen.z)
        )

      // DESKTOP
      if (!this.$device.isTouch) {
        // fake box that represente screens
        const box = new Box3(new Vector3(30, -15, -193), new Vector3(162, 80, -132))
        this.cameraPosBase = this.fitTo.fit(box, {bottom: .3, top: .3, left: .6, right: .1}, {vector: new Vector3(-.8, .3, 2)})
        this.cameraMovement.setCameraPosition(0, {position: this.cameraPosBase.position, lookAt: this.cameraPosBase.look})

        this.cameraPosScreen = this.fitTo.fit(screenBox, {
          top: .30,
          right: 0,
          bottom: .55,
          left: 0
        }, {vector: new Vector3(0, .04, 1)})
        this.cameraMovement.setCameraPosition(1, {position: this.cameraPosScreen.position, lookAt: this.cameraPosScreen.look})

        this.cameraEnd = {
          position: this.cameraPosScreen.position.clone(),
          look: this.cameraPosScreen.look.clone()
        }
        this.cameraEnd.position.x *= 1.5
        this.cameraEnd.look.x *= 1.5
        this.cameraMovement.setCameraPosition(2, {position: this.cameraEnd.position, lookAt: this.cameraEnd.look})
      }
      // MOBILE
      else {
        this.cameraPosBase = this.fitTo.fit(this.ball.boundingBox, {bottom: 5, top: 20, left: 10, right: 10}, {vector: new Vector3(0, .3, 2)})
        this.cameraMovement.setCameraPosition(0, {position: this.cameraPosBase.position, lookAt: this.cameraPosBase.look})

        if (!this.$device.isIphone) {
          this.cameraMovement.setCameraPosition(1, {
            position: new Vector3(124, 40, 340),
            lookAt: new Vector3(124, 30, 0)
          })
        }

        this.cameraPosScreen = this.fitTo.fit(screenBox, .1, {vector: new Vector3(0, .04, 1)})
        this.cameraMovement.setCameraPosition(this.$device.isIphone ? 1 : 2, {position: this.cameraPosScreen.position, lookAt: this.cameraPosScreen.look})

        this.cameraEnd = {
          position: this.cameraPosScreen.position.clone(),
          look: this.cameraPosScreen.look.clone()
        }
        this.cameraEnd.position.x *= 1.5
        this.cameraEnd.look.x *= 1.5
        this.cameraMovement.setCameraPosition(this.$device.isIphone ? 2 : 3, {position: this.cameraEnd.position, lookAt: this.cameraEnd.look})
      }

      this.visibleViewport = visibleViewport(webGL.camera, new Vector3(webGL.camera.position.x, webGL.camera.position.y, this.ball.position.z)).width
      this.visibleViewportScreen = visibleViewport(webGL.camera, this.cameraPosScreen.look, this.cameraPosScreen.position).width

      if (this.isIntroduction) {
        webGL.camera.position.copy(this.cameraPosBase.position)
        webGL.camera.look.copy(this.cameraPosBase.look)
      }
    },

    onNext () {
      this.cameraMovement.onNext()
    },

    loadEnvMap () {
      return new Promise( (resolve, reject) => {
        const path = "/images/cubemap/"
        const format = '.png'
        const urls = [
          path + 'px' + format, path + 'nx' + format,
          path + 'py' + format, path + 'ny' + format,
          path + 'pz' + format, path + 'nz' + format
        ]

        const textureCube = new CubeTextureLoader().load( urls, resolve )
      })
    }
  }
}
</script>

<style lang="stylus">
@import '~@/styles/settings/variables'
@import '~@/styles/settings/easings'
@import '~@/styles/settings/mixins/mq'
@import '~@/styles/settings/mixins/viewport-sizer'

.home
  &__title
    font-family circular
    font-weight: $weights.bold
    viewportSizer('font-size', 70, 1920)
    viewportSizer('line-height', 90, 1920)

    +mq($until: 'tablet')
      viewportSizer('font-size', 55, 767)
      viewportSizer('line-height', 60, 767)

    +mq($until: 'mobile')
      viewportSizer('font-size', 35, 576)
      viewportSizer('line-height', 40, 576)

  &__cta
    &--mobile
      position absolute
      bottom $spacings.md
      left 50%
      transform translateX(-50%)
    
    position absolute
    bottom 0
    padding-bottom 70px
    left 50%
    transform translateX(-50%)
          
    &:after
      content ''
      position absolute
      bottom 0
      left calc(50% - 1px)
      width 2px
      height 50px
      background-color $colors.white
      animation 10s 3s scrollAnim ease-in-out-quart infinite
        
      // transform-origin 50% 100%
      // transform scaleY(1) scaleZ(0)

@keyframes scrollAnim
  0%
    transform-origin 50% 100%
    transform scaleY(1) translateZ(0)
  4.5%
    transform-origin 50% 100%
    transform scaleY(0) translateZ(0)
  5.5%
    transform-origin 50% 0%
    transform scaleY(0) translateZ(0)
  10%
    transform-origin 50% 0%
    transform scaleY(1) translateZ(0)
</style>